import { fetchAuthSession, getCurrentUser } from 'aws-amplify/auth';
import axios from 'axios';
import dayjs from 'dayjs';
import { RouteLocationNormalized, useRoute } from 'vue-router';

import { CREDIT_CARD_GOALS, DISMISSED_REPORT_INSIGHTS_KEY, MOCK_ADDRESS_MAP, POSITIVE_REPORT_INSIGHTS_KEY } from '@/constants/constants';
import {
  MSE_CARDS_QUESTION_SET,
  MSE_CARDS_RESULTS,
  MSE_LOANS_QUESTION_SET,
  MSE_LOANS_RESULTS,
} from '@/constants/urls';
import { CREDIT_REPORT_ASSET_ID, ELIGIBILITY_SCORES, URL_PARAMS } from '@/types/enums';
import { ErrorData } from '@/types/interfaces';
import { CardDetails } from '@/types/requestData';
import { CreditReportAsset } from '@/types/responseTypes';
import { MockTestOtpAddress } from '@/utils/dataMocks';

export const getCookie = (name: string): string | undefined => {
  const cookies = document.cookie.split(';');

  return Object.fromEntries(cookies.map((cookie) => cookie.trim().split('=')))[name];
};

export const validatePassword = (pass: string | undefined): boolean => {
  if (!pass) return true;

  const MANDATORY_CHARS = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).+$/;
  const ALLOWED_CHARS = /^[a-zA-Z0-9\u00c0-\u024f\u1e00-\u1eff!"\\#$%&'()*+,\-./:;<=>?@^_`\\{|}~]*$/;

  return MANDATORY_CHARS.test(pass) && ALLOWED_CHARS.test(pass);
};

export const roundPercentage = (percentage: number) => {
  if (percentage < 1) return Math.ceil(percentage);
  if (percentage > 99) return Math.floor(percentage);
  return Math.round(percentage);
};

export const formatDate = (date: number | string | Date) => dayjs(date).format('DD MMM YYYY');

export const currencyFormat = (value: number | string, options = {}): string => {
  const args: Intl.NumberFormatOptions = {
    style: 'currency' as const,
    currency: 'GBP',
    currencyDisplay: 'symbol',
    useGrouping: true,
    ...options,
  };

  const numberFormat = new Intl.NumberFormat('en-GB', args).format(
    Number.isNaN(parseFloat(value.toString())) ? 0 : +value,
  );

  return numberFormat.replace(/\.00$/, '');
};

export const addDaysToDateAndFormat = (date: string | Date, dayCount: number) =>
  dayjs(date).add(dayCount, 'day').format('DD MMM YYYY');

export const calcAge = (birthday: string | undefined): number => {
  if (!birthday || dayjs(birthday).year() > dayjs().year()) return 0;
  const monthDiff = +dayjs() - +dayjs(birthday);

  return Math.abs(new Date(monthDiff).getUTCFullYear() - 1970);
};

export const isValidDate = (date: string | undefined): boolean => {
  if (!date) return false;
  return !Number.isNaN(+new Date(date));
};

export const isAvailableLeapYearDate = (date: string | undefined): boolean => {
  if (!date) return true;
  const [year, month, day] = date.split('-');

  if (month === '02' && day === '29') return new Date(+year, 1, 29).getDate() === 29;
  return true;
};

export const nonNumberCharsRemover = (str: string): string => {
  return str.replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
};

export const nonIntegerCharsRemover = (str: string): string => {
  return str.replace(/\D/g, '');
};

export const standardiseText = (val: string): string => val.replace(/\s/g, '').toLowerCase();

export const splitMarkedText = (
  toSplit: string,
  markedText: string,
): { value: string; marked?: boolean }[] => {
  const standardizedMarkedText = standardiseText(markedText);

  if (!standardizedMarkedText) {
    return [{ value: toSplit }];
  }
  const regex = new RegExp(standardizedMarkedText.split('').join('\\s*'), 'gi');
  const matchSeparator = '$%separator%$';
  const strWithSeparators = toSplit.replace(regex, (match) => {
    return matchSeparator + match + matchSeparator;
  });
  const textArray = strWithSeparators.split(matchSeparator);

  return textArray.map((t) => {
    return {
      value: t,
      marked: standardiseText(t) === standardizedMarkedText,
    };
  });
};

export const toSentenceCase = (text: string): string => {
  const pattern = /(^\s*\w|\.\s*\w)/gi;

  return text.replace(pattern, (toReplace) => {
    return toReplace.toUpperCase();
  });
};

export const isJsonString = (str: unknown): boolean => {
  try {
    JSON.parse(str as string);
  } catch (e) {
    return false;
  }
  return true;
};

export const getEligibilityClass = (score: number): number | 'na' => {
  if (score === ELIGIBILITY_SCORES.PREAPPROVED) return ELIGIBILITY_SCORES.PREAPPROVED;
  if (score >= ELIGIBILITY_SCORES.VSTRONG) return ELIGIBILITY_SCORES.VSTRONG;
  if (score >= ELIGIBILITY_SCORES.STRONG) return ELIGIBILITY_SCORES.STRONG;
  if (score >= ELIGIBILITY_SCORES.FAIR) return ELIGIBILITY_SCORES.FAIR;
  if (score >= ELIGIBILITY_SCORES.WEAK) return ELIGIBILITY_SCORES.WEAK;
  if (score >= ELIGIBILITY_SCORES.VWEAK) return ELIGIBILITY_SCORES.VWEAK;
  if (score === ELIGIBILITY_SCORES.SPECIAL) return ELIGIBILITY_SCORES.FAIR;
  if (score === ELIGIBILITY_SCORES.NA) return 'na';
  if (score === ELIGIBILITY_SCORES.NO_CHANCE) return ELIGIBILITY_SCORES.VWEAK;
  return 'na';
};

export const eligibilityDescription = (score: number, eligibilityPercentage: string): string => {
  if (score === ELIGIBILITY_SCORES.SPECIAL) {
    return `Unlikely - You've got a ${eligibilityPercentage} chance of getting this card`;
  }
  if (score === ELIGIBILITY_SCORES.NO_CHANCE) {
    return 'You are not eligible for this card';
  }
  if (score === ELIGIBILITY_SCORES.PREAPPROVED) {
    return 'You will get this card, and the exact offer shown, subject to ID checks & if the info you gave is correct';
  }
  if (score >= ELIGIBILITY_SCORES.VSTRONG) {
    return `Very strong chance - ${eligibilityPercentage} approval rate for this card`;
  }
  if (score >= ELIGIBILITY_SCORES.STRONG) {
    return `Strong chance - ${eligibilityPercentage} approval rate for this card`;
  }
  if (score >= ELIGIBILITY_SCORES.FAIR) {
    return `Fair chance - ${eligibilityPercentage} approval rate for this card`;
  }
  if (score > ELIGIBILITY_SCORES.VWEAK) {
    return `Weak chance - ${eligibilityPercentage} approval rate for this card`;
  }
  return '';
};

export const eligibilityTextTitleMobile = (score: number): string => {
  if (score === ELIGIBILITY_SCORES.SPECIAL) {
    return 'Unlikely';
  }
  if (score === ELIGIBILITY_SCORES.NO_CHANCE) {
    return 'You are not eligible for this card';
  }
  if (score === ELIGIBILITY_SCORES.PREAPPROVED) {
    return 'You will get this card, and the exact offer shown, subject to ID checks & if the info you gave is correct';
  }
  if (score >= ELIGIBILITY_SCORES.VSTRONG) {
    return 'Very strong chance';
  }
  if (score >= ELIGIBILITY_SCORES.STRONG) {
    return 'Strong chance';
  }
  if (score >= ELIGIBILITY_SCORES.FAIR) {
    return 'Fair chance';
  }
  if (score > ELIGIBILITY_SCORES.VWEAK) {
    return 'Weak chance';
  }
  return '';
};

export const eligibilityTextMobile = (score: number): string => {
  if (score === ELIGIBILITY_SCORES.SPECIAL) {
    return "At the moment, we can't be more accurate, but we're looking to refine this.";
  }
  if (score === ELIGIBILITY_SCORES.NO_CHANCE) {
    return "Based on the info you've entered, you will not be accepted. This could be due to your age, or who you bank with, or other criteria this lender has set out.";
  }
  if (score === ELIGIBILITY_SCORES.PREAPPROVED) {
    return "You will get this card, and the exact offer shown, subject to ID checks & if the info you gave is correct - You're pre-approved, so if you choose to apply you don't need to be credit checked for this product. See the pre-approval FAQ for more info.";
  }
  if (score > ELIGIBILITY_SCORES.VWEAK) {
    return `${score * 10
      } people in every hundred who apply with your circumstances are likely to get the card.`;
  }
  return 'Unfortunately, we are not able to provide likelihoods for this card. See the FAQs below for more information.';
};

export const formatCurrency = (amount: number | string): string => {
  return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

export const wait = async (delay: number): Promise<1> => {
  return new Promise((resolve) => {
    setTimeout(() => resolve(1), delay);
  });
};

export const createRandomDelay = (max: number, min = 1000): number => {
  const formattedMin = Math.ceil(min);
  const formattedMax = Math.floor(max);

  return Math.floor(Math.random() * (formattedMax - formattedMin + 1)) + formattedMin;
};

export const insertImageUrl = (input: string, match: string, emoji: string) => {
  return input.replace(match, `<i class="emoji ${emoji}"></i>`);
};

export const parseEmoji = (text: string) => {
  let result = text;
  const matches = result.match(/\[(.*?)\]/g);

  if (matches) {
    Array.prototype.forEach.call(matches, (match) => {
      if (match === '[airplane emoji]') result = insertImageUrl(result, match, 'airplane');
      if (match === '[credit card emoji]') result = insertImageUrl(result, match, 'creditcard');
      if (match === '[folded hands emoji]') result = insertImageUrl(result, match, 'foldedhands');
      if (match === '[party popper emoji]') result = insertImageUrl(result, match, 'partypopper');
      if (match === '[pound banknote emoji]')
        result = insertImageUrl(result, match, 'poundbanknote');
      if (match === '[robot face emoji]') result = insertImageUrl(result, match, 'robotface');
      if (match === '[slightly smiling face emoji]')
        result = insertImageUrl(result, match, 'slightlysmilingface');
      if (match === '[smiling face with sunglasses emoji]')
        result = insertImageUrl(result, match, 'smilingfacewithsunglasses');
      if (match === '[thumbs up emoji]') result = insertImageUrl(result, match, 'thumbsup');
      if (match === '[waving hand emoji]') result = insertImageUrl(result, match, 'wavinghand');
    });
  }

  return result;
};

export const getAuthToken = async (): Promise<string> => {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? '';
  } catch {
    return '';
  }
};

export const generateRandId = (): string => {
  return Math.random()
    .toString(36)
    .replace(/[^a-z]+/g, '')
    .substr(2, 10);
};

export const decodeJWT = (token: string): Record<string, unknown> => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => {
        return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
      })
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const checkIsAuthorizedInCognito = async () => {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
};

export const getCardGoalIds = () => {
  return CREDIT_CARD_GOALS.map((goal) => goal.goalId);
};

export const findByKeyValue = (objectToSearch, key, value) =>
  objectToSearch.find((option) => option[key] === value);

export const transformCardDetails = (cardDetails): CardDetails => {
  return {
    immediateCashWithdrawal: cardDetails.immediateCashWithdrawal === 'true',
    balanceTransferAmount:
      cardDetails.balanceTransferAmount === '' ? null : Number(cardDetails.balanceTransferAmount),
  };
};

export const getMockAddress = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const testJourney = urlParams.get('testJourney') || 'otp';
  return MOCK_ADDRESS_MAP[testJourney] || MockTestOtpAddress;
};

export const getMseProductRedirectUrls = (product: string | null) => {
  const isLoans = product === 'loans';

  return {
    productLabel: isLoans ? 'Loans' : 'Cards',
    resultsUrl: isLoans ? MSE_LOANS_RESULTS : MSE_CARDS_RESULTS,
    questionSetUrl: isLoans ? MSE_LOANS_QUESTION_SET : MSE_CARDS_QUESTION_SET,
  };
};

export const isDateOver30DaysAgo = (dateString: string): boolean => {
  const date = new Date(dateString);
  const now = new Date();
  const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

  return date < thirtyDaysAgo;
};

export const dateToIso = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toISOString();
};

export const areObjectsEqual = (obj1, obj2): boolean => {
  if (obj1 === obj2) return true;

  if (obj1 === null || obj2 === null || typeof obj1 !== 'object' || typeof obj2 !== 'object') {
    return false;
  }

  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) return false;

  return keys1.every((key) => {
    if (!keys2.includes(key)) return false;
    return areObjectsEqual(obj1[key], obj2[key]);
  });
};

export const getCreditCardsEligibilityParams = (componentRoute?: RouteLocationNormalized) => {
  const route = componentRoute || useRoute();

  const query = route?.query || {};

  const VALID_GOAL_IDS = CREDIT_CARD_GOALS.map((g) => g.goalId) as string[];
  const VALID_BOOLEAN_PARAMS = ['true', 'false'];

  const goalParam = String(query[URL_PARAMS.GOAL] ?? '');
  const goal = VALID_GOAL_IDS.includes(goalParam) ? goalParam : '';

  const balanceTransferAmount = String(query[URL_PARAMS.BALANCE_TRANSFER_AMOUNT] ?? '');

  const cashWithdrawalParam = String(query[URL_PARAMS.CASH_WITHDRAWAL] ?? '');
  const cashWithdrawal = VALID_BOOLEAN_PARAMS.includes(cashWithdrawalParam) ? cashWithdrawalParam : null;

  const cardIntent = String(query[URL_PARAMS.CARD_INTENT] ?? '');

  const queryParamsExist =
    goal !== '' && cashWithdrawal !== null;

  return {
    goal,
    balanceTransferAmount,
    cashWithdrawal,
    queryParamsExist,
    cardIntent
  };
};

export const getRequestError = ({ url, error }: { url: string, error: unknown }): { logMessage: string; errorData: ErrorData } => {
  if (axios.isAxiosError(error)) {
    return {
      logMessage: `request failed - ${error.message}`,
      errorData: {
        message: error.message,
        data: error.response?.data,
        status: error.response?.status,
        url,
      },
    };
  } else {
    const genericError = error as Error;
    const errorMessage = genericError.message || 'Unknown error';
    return {
      logMessage: `unexpected error - ${errorMessage}`,
      errorData: {
        message: errorMessage,
        stack: genericError.stack || genericError.toString(),
        name: genericError.name,
        url,
      },
    };
  }
};

export const getGlobalAuthPath = (): string => {
  const { pathname, search } = window.location;
  const target = search ? `${pathname}${search}` : pathname;
  return `/login?target=${encodeURIComponent(target)}`;
};

export const getReportInsightsAssets = (assets?: CreditReportAsset[]) => {
  const reportInsightsAsset = assets?.find(
    (asset) => asset?.id === CREDIT_REPORT_ASSET_ID.REPORT_INSIGHTS
  );

  if (!reportInsightsAsset) return undefined;

  const dismissedIds = getDismissedReportInsights();
  if (!dismissedIds.length) return reportInsightsAsset;

  return {
    ...reportInsightsAsset,
    elements:
      reportInsightsAsset.elements?.filter((element) => !dismissedIds.includes(element.id)) ?? []
  };
};

export const getDismissedReportInsights = (): string[] => {
  return parseStringArrayFromLocalStorage(DISMISSED_REPORT_INSIGHTS_KEY);
};

export const getPositiveReportInsights = (): string[] => {
  return parseStringArrayFromLocalStorage(POSITIVE_REPORT_INSIGHTS_KEY);
};

const parseStringArrayFromLocalStorage = (key: string): string[] => {
  const storedValue = localStorage.getItem(key);
  if (!storedValue) return [];
  try {
    const parsed = JSON.parse(storedValue);
    if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
      return parsed;
    }
  } catch {
    // fall through to return []
  }
  return [];
};

export const formatPercentageWithDecimal = (value: number): string => {
  if (value === null || value === undefined || isNaN(value)) {
    return '—';
  }

  const roundedRate = Math.round((Number(value) + Number.EPSILON) * 100) / 100;

  if (roundedRate === 0) {
    return '0%';
  }

  if (Number.isInteger(roundedRate)) {
    return `${roundedRate.toFixed(1)}%`;
  }

  return `${roundedRate}%`;
};
